import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Segment } from '../../../shared/types/segment.interface';
import { LocationApiResponse } from '../../../shared/types';
import {
  formatCampaignPlatformList,
  getCampaignPlatformIds,
} from '../../../services/campaign.service';

/**
 * Modal component that previews a campaign's details, validates section completeness, and provides navigation and publish actions.
 * This component renders a summary of a campaign (name, description, platforms, segments, locations, offer details, schedule, goals, and content availability),
 * formats values for display, checks whether required sections are completed (including platform-specific sections), and emits events to close the modal,
 * publish the campaign, or navigate back to specific editor tabs for editing.
 * Key methods:
 *   - onClose(): emits close event to request closing the modal.
 *   - onConfirmAndPublish(): emits confirmAndPublish event to trigger publishing.
 *   - isAllSectionsCompleted(): returns boolean indicating whether required core and selected platform sections are completed.
 *   - navigateTo<Section>(): a set of navigation helpers that emit navigateToTab with a tabId and section to jump back to the editor.
 *   - getCampaignName(), getCampaignDescription(), getPlatforms(), getUserSegments(), getLocations(), getOfferName(), getPromoCode(), getOfferDescription(), getOfferType(), getDiscountValue(), getCarWashType(), getLocation(), getMaxDiscountAmount(), getQuantityLimit(), getGoals(), getFirstGoal(): format and return campaign display values.
 *   - getEventLabel(goal) and getTimeframeLabel(goal): map internal values to human readable labels.
 *   - getStartDate(), getStartTime(), getEndDate(), getEndTime(), getRecurringFrequency(): schedule formatting helpers.
 *   - isPlatformSelected(platform) and hasContent(type): helpers to detect selected platforms and whether required content exists for a given channel.
 * Lifecycle hooks: none implemented on the component itself; it is input-driven and reacts via bindings and emitted events.
 * @component
 * @selector app-campaign-preview-modal
 * @example
 *   <app-campaign-preview-modal
 *     [isOpen]="true"
 *     [campaignData]="campaign"
 *     [sectionCompletionStatus]="sectionStatus"
 *     [segmentsList]="segments"
 *     [locationsList]="locations"
 *     [isPublishing]="isSaving"
 *     (close)="onPreviewClose()"
 *     (confirmAndPublish)="onPublishConfirm()"
 *     (navigateToTab)="onNavigateToTab($event)">
 *   </app-campaign-preview-modal>
 * @input boolean isOpen - Controls whether the preview modal is visible.
 * @input any campaignData - The campaign object to preview (contains name, description, platforms, segments, locations, offer and schedule data).
 * @input { [key: string]: boolean } sectionCompletionStatus - Map of section names to completion boolean used to determine completeness.
 * @input Segment[] segmentsList - Optional list of segment objects used to resolve segment names from IDs when presenting segments.
 * @input LocationApiResponse[] locationsList - Optional list of location objects used to resolve branch names from IDs when presenting locations.
 * @input boolean isPublishing - Indicates publish action is in progress (used to disable UI/confirm button).
 * @output EventEmitter<void> close - Emitted when the modal should be closed (user clicks close/cancel).
 * @output EventEmitter<void> confirmAndPublish - Emitted when the user confirms publishing the campaign.
 * @output EventEmitter<{tabId: string, section?: string}> navigateToTab - Emitted to request navigation back to a specific editor tab and section for editing.
 */
@Component({
  selector: 'app-campaign-preview-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './campaign-preview-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign-preview-modal.component.scss',
})
export class CampaignPreviewModalComponent {
  @Input() isOpen = false;
  @Input() campaignData: any = null;
  @Input() sectionCompletionStatus: Record<string, boolean> = {};
  @Input() segmentsList: Segment[] = [];
  @Input() locationsList: LocationApiResponse[] = [];
  @Input() isPublishing = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirmAndPublish = new EventEmitter<void>();
  @Output() navigateToTab = new EventEmitter<{ tabId: string; section?: string }>();

  onClose(): void {
    this.close.emit();
  }

  onConfirmAndPublish(): void {
    this.confirmAndPublish.emit();
  }

  // Method to check if all sections are completed
  /**
   * Check whether all required campaign sections are completed (core sections and any selected platform-specific sections).
   * @example
   * this.sAllSectionsCompleted()
   * true
   * @returns {boolean} True if all required sections are completed, false otherwise.
   **/
  isAllSectionsCompleted(): boolean {
    const coreSections = [
      'Basic Info',
      'Platform',
      ...(this.showUserSegmentSection ? ['User Segment'] : []),
      'Offer Overview',
      'Offer Details',
      'Usage Limits',
      'Schedule',
    ];
    const coreCompleted = coreSections.every(
      section => this.sectionCompletionStatus[section] === true
    );

    // Check platform-specific sections only if the platform is selected
    const platformSections = [
      { platform: 'email', section: 'Email' },
      { platform: 'sms', section: 'Text Message' },
      { platform: 'facebook', section: 'Facebook' },
      { platform: 'google', section: 'Google Ads' },
    ];

    const platformSectionsCompleted = platformSections.every(({ platform, section }) => {
      // If platform is not selected, consider it completed (skip it)
      if (!this.isPlatformSelected(platform)) {
        return true;
      }
      // If platform is selected, check if the section is completed
      return this.sectionCompletionStatus[section] === true;
    });

    return coreCompleted && platformSectionsCompleted;
  }

  // Navigation methods for edit buttons
  navigateToBasicInfo(): void {
    this.navigateToTab.emit({ tabId: 'overview', section: 'Basic Info' });
  }

  navigateToPlatform(): void {
    this.navigateToTab.emit({ tabId: 'overview', section: 'Platform' });
  }

  navigateToUserSegment(): void {
    this.navigateToTab.emit({ tabId: 'overview', section: 'User Segment' });
  }

  navigateToOfferOverview(): void {
    this.navigateToTab.emit({ tabId: 'offers', section: 'Offer Overview' });
  }

  navigateToOfferDetails(): void {
    this.navigateToTab.emit({ tabId: 'offers', section: 'Offer Details' });
  }

  navigateToUsageLimits(): void {
    this.navigateToTab.emit({ tabId: 'offers', section: 'Usage Limits' });
  }

  navigateToEmail(): void {
    this.navigateToTab.emit({ tabId: 'content', section: 'Email' });
  }

  navigateToTextMessage(): void {
    this.navigateToTab.emit({ tabId: 'content', section: 'Text Message' });
  }

  navigateToFacebook(): void {
    this.navigateToTab.emit({ tabId: 'content', section: 'Facebook' });
  }

  navigateToGoogleAds(): void {
    this.navigateToTab.emit({ tabId: 'content', section: 'Google Ads' });
  }

  navigateToGoals(): void {
    this.navigateToTab.emit({ tabId: 'finish', section: 'Goals' });
  }

  navigateToSchedule(): void {
    this.navigateToTab.emit({ tabId: 'finish', section: 'Schedule' });
  }

  // Helper methods to format data for display
  getCampaignName(): string {
    return this.campaignData?.name || '-';
  }

  getCampaignDescription(): string {
    return this.campaignData?.description || '-';
  }

  getPlatforms(): string {
    const formatted = formatCampaignPlatformList(this.campaignData?.platform);
    return formatted || '-';
  }

  isPlatformSelected(platform: string): boolean {
    return getCampaignPlatformIds(this.campaignData?.platform).includes(platform);
  }

  get showUserSegmentSection(): boolean {
    return this.isPlatformSelected('email') || this.isPlatformSelected('sms');
  }

  /**
   * Returns a comma-separated string of segment names (or IDs) for the current campaign, or '-' if there are no segments.
   * @example
   * etUserSegments()
   * "Marketing, Beta Users"
   * @returns {string} Comma-separated segment names or IDs, or '-' when no segments are available.
   **/
  getUserSegments(): string {
    if (!this.campaignData?.segment || this.campaignData.segment.length === 0) {
      return '-';
    }

    // If segments are objects with names, extract names
    if (this.campaignData.segment[0] && typeof this.campaignData.segment[0] === 'object') {
      return this.campaignData.segment.map((s: any) => s.name).join(', ');
    }

    // If we have a segments list, map IDs to names
    if (this.segmentsList && this.segmentsList.length > 0) {
      const segmentNames = this.campaignData.segment.map((segmentId: string) => {
        const segment = this.segmentsList.find(s => s.id === segmentId);
        return segment ? segment.name : segmentId; // Fallback to ID if segment not found
      });
      return segmentNames.join(', ');
    }

    // Fallback to showing IDs if no segments list available
    return this.campaignData.segment.join(', ');
  }

  /**
   * Returns a comma-separated list of campaign location branch names or '-' if no displayable locations exist.
   * @example
   * etLocations()
   * 'Main Branch, East Branch'
   * @returns {string} Human-readable list of locations or '-' when no locations are available.
   **/
  getLocations(): string {
    if (!this.campaignData?.locations || this.campaignData.locations.length === 0) {
      return '-';
    }

    // Filter out "allLocations" string if present (it's only for UI state, not for display)
    const filteredLocations = this.campaignData.locations.filter(
      (loc: any) => loc !== 'allLocations'
    );

    if (filteredLocations.length === 0) {
      return '-';
    }

    // If locations are objects with branch names, extract branch names
    if (filteredLocations[0] && typeof filteredLocations[0] === 'object') {
      return filteredLocations.map((l: any) => l.branch).join(', ');
    }

    // If we have a locations list, map IDs to branch names
    if (this.locationsList && this.locationsList.length > 0) {
      const locationNames = filteredLocations.map((locationId: string) => {
        const location = this.locationsList.find(l => l.id === locationId);
        return location ? location.branch : locationId; // Fallback to ID if location not found
      });
      return locationNames.join(', ');
    }

    // Fallback to showing IDs if no locations list available
    return filteredLocations.join(', ');
  }

  getOfferName(): string {
    return this.campaignData?.offer_name || '-';
  }

  getPromoCode(): string {
    return this.campaignData?.promo_code || '-';
  }

  getOfferDescription(): string {
    return this.campaignData?.offer_description || '-';
  }

  getOfferType(): string {
    if (this.campaignData?.offer_type === 'flat_discount') {
      return 'Flat Discount';
    }
    if (this.campaignData?.offer_type === 'way_earning') {
      return 'Way Earning (Cashback)';
    }

    return '-';
  }

  getDiscountValue(): string {
    return this.campaignData?.offer_discount || '-';
  }

  /**
   * Return a human-readable car wash offer type derived from the component's campaignData.
   * @example
   * this.etCarWashType()
   * '-'
   * @returns {string} The formatted wash type: '-' if offer_wash_type is not set, 'single wash, monthly subscriptions' if the value is 'any', otherwise the offer_wash_type value with underscores replaced by spaces.
   **/
  getCarWashType(): string {
    if (!this.campaignData?.offer_wash_type) {
      return '-';
    }

    const washType = this.campaignData.offer_wash_type;

    // If value is "any", show both options
    if (washType === 'any') {
      return 'single wash, monthly subscriptions';
    }

    // Replace underscores with spaces
    return washType.replace(/_/g, ' ');
  }

  getLocation(): string {
    return this.getLocations();
  }

  getMaxDiscountAmount(): string {
    return this.campaignData?.max_discount ? `$${this.campaignData.max_discount}` : '-';
  }

  getQuantityLimit(): string {
    return this.campaignData?.quantity_limit || '-';
  }

  getGoals(): any[] {
    return this.campaignData?.goals || [];
  }

  getFirstGoal(): any {
    const goals = this.getGoals();
    return goals.length > 0 ? goals[0] : null;
  }

  /**
   * Convert a goal.event identifier into a human-readable event label.
   * @example
   * etEventLabel({ event: 'car-washes-booked' })
   * 'Car Washes Booked'
   * @param {any} goal - Goal object that may contain an 'event' property.
   * @returns {string} Human-readable event label, '-' if no event, or the raw event string if not matched.
   */
  getEventLabel(goal: any): string {
    if (!goal?.event) return '-';

    const eventOptions = [
      { label: 'Car Washes Booked', value: 'car-washes-booked' },
      { label: 'Offer Used', value: 'offer-used' },
      { label: 'Purchased Subscriptions', value: 'purchased-subscriptions' },
      { label: 'Clicked On Email', value: 'clicked-on-email' },
      { label: 'Clicked On Text Messaging', value: 'clicked-on-text-messaging' },
      { label: 'App Opened', value: 'app-opened' },
    ];

    const option = eventOptions.find(opt => opt.value === goal.event);
    return option ? option.label : goal.event;
  }

  /**
   * Return a human-readable label for a campaign goal's timeframe.
   * @example
   * etTimeframeLabel({ timeframe: '7-days' })
   * '7 Days'
   * @param {any} goal - The goal object which may contain a 'timeframe' property.
   * @returns {string} Human-readable label for the timeframe, or '-' if not set.
   */
  getTimeframeLabel(goal: any): string {
    if (!goal?.timeframe) return '-';

    const timeframeOptions = [
      { label: 'Campaign Duration', value: 'campaign-duration' },
      { label: '7 Days', value: '7-days' },
      { label: '14 Days', value: '14-days' },
      { label: '30 Days', value: '30-days' },
      { label: 'Custom Duration', value: 'custom-duration' },
    ];

    const option = timeframeOptions.find(opt => opt.value === goal.timeframe);
    return option ? option.label : goal.timeframe;
  }

  /**
   * Format a schedule instant as an en-US 'MMM D, YYYY' date, or '-' when missing/unparseable.
   *
   * The schedule_*_date instants are the only source of truth for what the user picked.
   * schedule_start_time is a SERVER-LOCAL projection of schedule_start_date that the API
   * derives for the scheduler's TIME column, and schedule_end_time is not persisted at all,
   * so neither may be used to render a date or a time: on a UTC server they render an IST
   * 5:00 PM pick as 11:30 AM. The instant itself renders correctly in any browser zone.
   */
  private formatScheduleDate(instant: string | Date | null | undefined): string {
    const date = this.parseScheduleInstant(instant);
    if (!date) return '-';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /** Format a schedule instant as an en-US 'hh:mm AM/PM' time, or '-' when missing/unparseable. */
  private formatScheduleTime(instant: string | Date | null | undefined): string {
    const date = this.parseScheduleInstant(instant);
    if (!date) return '-';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  private parseScheduleInstant(instant: string | Date | null | undefined): Date | null {
    if (!instant) return null;
    const date = new Date(instant);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Return the campaign's scheduled start date formatted for the en-US locale, or '-' if unavailable or invalid.
   * @example
   * etStartDate()
   * Mar 1, 2024
   * @returns {string} Formatted start date in 'MMM D, YYYY' (en-US) or '-' when the date is missing or invalid.
   */
  getStartDate(): string {
    return this.formatScheduleDate(this.campaignData?.schedule_start_date);
  }

  /**
   * Returns the campaign start time extracted from campaignData.schedule_start_date as a localized "hh:mm AM/PM" string, or '-' if unavailable or invalid.
   * @example
   * etStartTime()
   * "08:30 AM"
   * @returns {string} The formatted start time (e.g., "08:30 AM") or '-' if not available or invalid.
   **/
  getStartTime(): string {
    return this.formatScheduleTime(this.campaignData?.schedule_start_date);
  }

  getRecurringFrequency(): string {
    return this.campaignData?.schedule_recurrence || '-';
  }

  /**
   * Return the campaign's end date formatted as 'Mon D, YYYY' or '-' if unavailable/invalid.
   * @example
   * etEndDate()
   * Jan 2, 2023
   * @returns {string} Formatted end date string (e.g., 'Jan 2, 2023') or '-' when no valid date exists.
   **/
  getEndDate(): string {
    return this.formatScheduleDate(this.campaignData?.schedule_end_date);
  }

  /**
   * Return the campaign's scheduled end time formatted as 'hh:mm AM/PM', or '-' if not set or invalid.
   * @example
   * this.etEndTime()
   * "02:30 PM"
   * @returns {string} Formatted end time in 'hh:mm AM/PM' or '-' when unavailable or invalid.
   */
  getEndTime(): string {
    return this.formatScheduleTime(this.campaignData?.schedule_end_date);
  }

  /**
   * Determine whether the campaign's contents include an entry of the given content type with required non-empty fields.
   * @example
   * asContent('email')
   * true
   * @param {{string}} {{type}} - Content type to check (e.g., 'email', 'sms', 'facebook', 'google_ads').
   * @returns {{boolean}} Return true if campaign data contains at least one content item of the specified type with required fields populated; otherwise false.
   **/
  hasContent(type: string): boolean {
    if (!this.campaignData?.contents) {
      return false;
    }

    // Handle different data structures
    if (Array.isArray(this.campaignData.contents)) {
      // Array structure - check for content types
      if (this.campaignData.contents.length === 0) {
        return false;
      }

      return this.campaignData.contents.some((content: any) => {
        if (type === 'email') {
          return (
            content.type === 'email' &&
            content.email_subject &&
            content.email_subject.trim() !== '' &&
            ((content.email_header && content.email_header.trim() !== '') ||
              (content.email_body_text && content.email_body_text.trim() !== '') ||
              (content.email_footer_name && content.email_footer_name.trim() !== ''))
          );
        }
        if (type === 'sms') {
          return content.type === 'sms' && content.sms_body && content.sms_body.trim() !== '';
        }
        if (type === 'facebook') {
          return (
            content.type === 'facebook' && content.fb_caption && content.fb_caption.trim() !== ''
          );
        }
        if (type === 'google_ads') {
          return (
            content.type === 'google_ads' &&
            content.g_ad_title &&
            content.g_ad_title.trim() !== '' &&
            content.g_ad_company &&
            content.g_ad_company.trim() !== ''
          );
        }
        return content.type === type;
      });
    }
    // Object structure - check for specific content types
    if (type === 'google_ads') {
      const googleContent = this.campaignData.contents.google;
      console.log('googleContent', googleContent);
      if (googleContent) {
        return (
          googleContent.g_ad_title ||
          googleContent.g_ad_title.trim() !== '' ||
          googleContent.g_ad_company ||
          googleContent.g_ad_company.trim() !== ''
        );
      }
      return false;
    }
    if (type === 'email') {
      const emailContent = this.campaignData.contents.email;
      if (emailContent) {
        return (
          emailContent.email_subject ||
          emailContent.email_subject.trim() !== '' ||
          emailContent.email_header ||
          emailContent.email_header.trim() !== '' ||
          emailContent.email_body_text ||
          emailContent.email_body_text.trim() !== '' ||
          emailContent.email_footer_name ||
          emailContent.email_footer_name.trim() !== ''
        );
      }
      return false;
    }
    if (type === 'sms') {
      const smsContent = this.campaignData.contents.sms;
      if (smsContent) {
        return smsContent.sms_body || smsContent.sms_body.trim() !== '';
      }
      return false;
    }
    if (type === 'facebook') {
      const facebookContent = this.campaignData.contents.facebook;
      if (facebookContent) {
        return facebookContent.fb_caption || facebookContent.fb_caption.trim() !== '';
      }
      return false;
    }

    return false;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
